/*
 * Customer Hub accessibility enhancements (WCAG 2.1 Level A: 2.1.1, 2.4.3, 4.1.2).
 * Progressive enhancement only: adds ARIA semantics, keyboard support and focus
 * management. No visual changes; customer-hub.js remains responsible for the
 * existing click behaviour (tab switching, modals, order receipts).
 */
(function () {
  'use strict';

  function closest(el, selector) {
    return el && el.closest ? el.closest(selector) : null;
  }

  /* Tabs: role=tablist/tab/tabpanel, aria-selected, roving tabindex, arrow keys */
  function initTabs() {
    var bar = document.querySelector('.hub-tabs-bar');
    if (!bar || bar.getAttribute('data-a11y-init') === 'true') return;
    bar.setAttribute('data-a11y-init', 'true');

    var tabs = Array.prototype.slice.call(bar.querySelectorAll('.hub-tab'));
    if (!tabs.length) return;

    bar.setAttribute('role', 'tablist');
    bar.setAttribute('aria-label', 'Customer Hub');

    tabs.forEach(function (tab) {
      var target = tab.getAttribute('data-target');
      var panel = target ? document.getElementById('hub-tab-' + target) : null;
      tab.setAttribute('role', 'tab');
      if (!tab.id && target) tab.id = 'hub-tab-btn-' + target;
      if (panel) {
        tab.setAttribute('aria-controls', panel.id);
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
      }
    });

    function sync() {
      var anySelected = false;
      tabs.forEach(function (tab) {
        var selected = tab.classList.contains('active');
        if (selected) anySelected = true;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.setAttribute('tabindex', selected ? '0' : '-1');
      });
      if (!anySelected) tabs[0].setAttribute('tabindex', '0');
    }

    sync();

    var observer = new MutationObserver(sync);
    tabs.forEach(function (tab) {
      observer.observe(tab, { attributes: true, attributeFilter: ['class'] });
    });

    bar.addEventListener('keydown', function (e) {
      var current = closest(e.target, '.hub-tab');
      if (!current) return;
      var index = tabs.indexOf(current);
      var next = null;
      switch (e.key) {
        case 'ArrowRight':
        case 'Right':
          next = tabs[(index + 1) % tabs.length];
          break;
        case 'ArrowLeft':
        case 'Left':
          next = tabs[(index - 1 + tabs.length) % tabs.length];
          break;
        case 'Home':
          next = tabs[0];
          break;
        case 'End':
          next = tabs[tabs.length - 1];
          break;
        default:
          return;
      }
      e.preventDefault();
      next.focus();
      next.click();
    });
  }

  /* Modals: dialog semantics, move focus in on open, Escape to close, keep Tab inside, restore focus */
  function initModals() {
    var modals = Array.prototype.slice.call(document.querySelectorAll('.hub-modal'));
    if (!modals.length) return;
    var lastTrigger = null;

    document.addEventListener('click', function (e) {
      var trigger = closest(e.target, '.js-add-address, .js-edit-address, .js-edit-profile');
      if (trigger) lastTrigger = trigger;
    }, true);

    function focusables(modal) {
      return Array.prototype.slice.call(
        modal.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter(function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });
    }

    modals.forEach(function (modal) {
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      var heading = modal.querySelector('h1, h2, h3');
      if (heading) {
        if (!heading.id) heading.id = modal.id + '-title';
        modal.setAttribute('aria-labelledby', heading.id);
      }

      new MutationObserver(function () {
        var isOpen = !modal.classList.contains('hidden');
        var wasOpen = modal.getAttribute('data-a11y-open') === 'true';
        if (isOpen && !wasOpen) {
          modal.setAttribute('data-a11y-open', 'true');
          var first = focusables(modal)[0];
          if (first) first.focus();
        } else if (!isOpen && wasOpen) {
          modal.removeAttribute('data-a11y-open');
          if (lastTrigger && document.body.contains(lastTrigger)) lastTrigger.focus();
        }
      }).observe(modal, { attributes: true, attributeFilter: ['class'] });
    });

    document.addEventListener('keydown', function (e) {
      var open = null;
      for (var i = 0; i < modals.length; i++) {
        if (!modals[i].classList.contains('hidden')) { open = modals[i]; break; }
      }
      if (!open) return;

      if (e.key === 'Escape' || e.key === 'Esc') {
        var closeBtn = open.querySelector('.js-close-modal');
        if (closeBtn) {
          e.preventDefault();
          closeBtn.click();
        }
        return;
      }

      if (e.key === 'Tab') {
        var items = focusables(open);
        if (!items.length) return;
        var first = items[0];
        var last = items[items.length - 1];
        if (!open.contains(document.activeElement)) {
          e.preventDefault();
          first.focus();
        } else if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  }

  /* Home tab order receipts: move focus to the revealed receipt and back again */
  function initOrderReceipts() {
    var lastViewButton = null;

    document.addEventListener('click', function (e) {
      var viewButton = closest(e.target, '#hub-tab-home .js-view-order');
      if (viewButton) {
        lastViewButton = viewButton;
        setTimeout(function () {
          var receipts = document.querySelectorAll('#hub-tab-home .home-order-receipt');
          for (var i = 0; i < receipts.length; i++) {
            if (receipts[i].getAttribute('data-order-id') === viewButton.getAttribute('data-order-id') && !receipts[i].classList.contains('hidden')) {
              var back = receipts[i].querySelector('.back-to-home-orders');
              if (back) back.focus({ preventScroll: true });
              break;
            }
          }
        }, 0);
        return;
      }

      var backButton = closest(e.target, '#hub-tab-home .back-to-home-orders');
      if (backButton && lastViewButton) {
        var restore = lastViewButton;
        setTimeout(function () {
          if (restore.offsetParent !== null) restore.focus();
        }, 0);
      }
    });
  }

  function init() {
    initTabs();
    initModals();
    initOrderReceipts();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
